import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Lock, Bell, Database, Save, Eye, EyeOff } from 'lucide-react';

const SettingsPage = ({ showToast }) => {
    const [activeSection, setActiveSection] = useState('profile');
    const [profile, setProfile] = useState({
        name: 'Admin User',
        email: 'admin@codebytz.com',
        role: 'Administrator'
    });

    const [security, setSecurity] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        newInquiries: true,
        newApplications: true,
        weeklyReport: false
    });

    useEffect(() => {
        // Load saved settings
        const savedProfile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const savedNotifications = JSON.parse(localStorage.getItem('admin_notifications') || '{}');

        if (Object.keys(savedProfile).length > 0) {
            setProfile({ ...profile, ...savedProfile });
        }
        if (Object.keys(savedNotifications).length > 0) {
            setNotifications({ ...notifications, ...savedNotifications });
        }
    }, [notifications, profile]);

    const handleProfileSave = (e) => {
        e.preventDefault();
        localStorage.setItem('admin_profile', JSON.stringify(profile));
        showToast?.('Profile updated successfully!', 'success');
    };

    const handlePasswordChange = (e) => {
        e.preventDefault();

        if (security.newPassword !== security.confirmPassword) {
            showToast?.('Passwords do not match!', 'error');
            return;
        }

        if (security.newPassword.length < 6) {
            showToast?.('Password must be at least 6 characters!', 'warning');
            return;
        }

        // In a real app, this would call an API
        showToast?.('Password changed successfully!', 'success');
        setSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' });
    };

    const handleNotificationsSave = () => {
        localStorage.setItem('admin_notifications', JSON.stringify(notifications));
        showToast?.('Notification preferences saved!', 'success');
    };

    const handleDataExport = () => {
        const allData = {
            inquiries: JSON.parse(localStorage.getItem('inquiries') || '[]'),
            projects: JSON.parse(localStorage.getItem('portfolio_projects') || '[]'),
            applications: JSON.parse(localStorage.getItem('job_applications') || '[]'),
            jobs: JSON.parse(localStorage.getItem('career_jobs') || '[]')
        };

        const dataStr = JSON.stringify(allData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `codebytz-backup-${new Date().toISOString().split('T')[0]}.json`;
        link.click();

        showToast?.('Data exported successfully!', 'success');
    };

    const sections = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'security', label: 'Security', icon: Lock },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'data', label: 'Data Management', icon: Database }
    ];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                    {sections.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeSection === section.id
                                    ? 'bg-brand-teal text-[#0a0a0a] font-bold'
                                    : 'text-gray-800 hover:gray/5 hover:text-black font-semibold'
                                }`}
                        >
                            <section.icon className="w-5 h-5" />
                            <span>{section.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
                <div className="bg-gray-200 border border-white/10 rounded-2xl p-8">
                    {/* Profile Settings */}
                    {activeSection === 'profile' && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <h2 className="text-2xl font-bold text-Black mb-6">Profile Settings</h2>
                            <form onSubmit={handleProfileSave} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2">Full Name</label>
                                    <input
                                        type="text"
                                        value={profile.name}
                                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                        className="w-full bg-white/5 border border-gray-500 rounded-xl px-4 py-3 text-black focus:border-brand-teal focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2">Email Address</label>
                                    <input
                                        type="email"
                                        value={profile.email}
                                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                        className="w-full bg-white/5 border border-gray-500 rounded-xl px-4 py-3 text-black focus:border-brand-teal focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2">Role</label>
                                    <input
                                        type="text"
                                        value={profile.role}
                                        disabled
                                        className="w-full bg-white/5 border border-gray-500 rounded-xl px-4 py-3 text-black cursor-not-allowed"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="flex items-center gap-2 px-6 py-3 bg-brand-teal text-[#0a0a0a] rounded-xl font-bold hover:bg-[#00d1c1] transition-all"
                                >
                                    <Save className="w-5 h-5" />
                                    Save Changes
                                </button>
                            </form>
                        </motion.div>
                    )}

                    {/* Security Settings */}
                    {activeSection === 'security' && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <h2 className="text-2xl font-bold text-black mb-6">Security Settings</h2>
                            <form onSubmit={handlePasswordChange} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2">Current Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPasswords.current ? 'text' : 'password'}
                                            value={security.currentPassword}
                                            onChange={(e) => setSecurity({ ...security, currentPassword: e.target.value })}
                                            className="w-full bg-white/5 border border-gray-500 rounded-xl px-4 py-3 pr-12 text-black focus:border-brand-teal focus:outline-none"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                        >
                                            {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2">New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPasswords.new ? 'text' : 'password'}
                                            value={security.newPassword}
                                            onChange={(e) => setSecurity({ ...security, newPassword: e.target.value })}
                                            className="w-full bg-white/5 border border-gray-500 rounded-xl px-4 py-3 pr-12 text-black focus:border-brand-teal focus:outline-none"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                        >
                                            {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2">Confirm New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPasswords.confirm ? 'text' : 'password'}
                                            value={security.confirmPassword}
                                            onChange={(e) => setSecurity({ ...security, confirmPassword: e.target.value })}
                                            className="w-full bg-white/5 border border-gray-500 rounded-xl px-4 py-3 pr-12 text-black focus:border-brand-teal focus:outline-none"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                        >
                                            {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="flex items-center gap-2 px-6 py-3 bg-brand-teal text-[#0a0a0a] rounded-xl font-bold hover:bg-[#00d1c1] transition-all"
                                >
                                    <Lock className="w-5 h-5" />
                                    Update Password
                                </button>
                            </form>
                        </motion.div>
                    )}

                    {/* Notification Settings */}
                    {activeSection === 'notifications' && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <h2 className="text-2xl font-bold text-Black mb-6">Notification Preferences</h2>
                            <div className="space-y-4">
                                {Object.entries(notifications).map(([key, value]) => (
                                    <div key={key} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-gray-500">
                                        <span className="text-black capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                        <button
                                            onClick={() => setNotifications({ ...notifications, [key]: !value })}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-brand-teal' : 'bg-gray-600'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={handleNotificationsSave}
                                    className="flex items-center gap-2 px-6 py-3 bg-brand-teal text-[#0a0a0a] rounded-xl font-bold hover:bg-[#00d1c1] transition-all mt-6"
                                >
                                    <Save className="w-5 h-5" />
                                    Save Preferences
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Data Management */}
                    {activeSection === 'data' && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <h2 className="text-2xl font-bold text-Black mb-6">Data Management</h2>
                            <div className="space-y-6">
                                <div className="p-6 bg-white/5 border border-gray-300 rounded-xl">
                                    <h3 className="text-lg font-bold text-Black mb-2">Export All Data</h3>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Download a complete backup of all your data including inquiries, projects, applications, and job postings.
                                    </p>
                                    <button
                                        onClick={handleDataExport}
                                        className="flex items-center gap-2 px-6 py-3 bg-brand-teal text-[#0a0a0a] rounded-xl font-bold hover:bg-[#00d1c1] transition-all"
                                    >
                                        <Database className="w-5 h-5" />
                                        Export Data
                                    </button>
                                </div>

                                <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl">
                                    <h3 className="text-lg font-bold text-red-400 mb-2">Danger Zone</h3>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Clearing data is permanent and cannot be undone. Make sure to export your data first.
                                    </p>
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to clear ALL data? This cannot be undone!')) {
                                                localStorage.clear();
                                                localStorage.setItem('isAdminAuthenticated', 'true');
                                                showToast?.('All data cleared successfully!', 'success');
                                                window.location.reload();
                                            }
                                        }}
                                        className="px-6 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-all"
                                    >
                                        Clear All Data
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
